import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plane, ClipboardList, AlertTriangle, FileWarning, ArrowLeft } from "lucide-react";

const STATUS_META = {
  normal: { label: "Serviceable", tile: "bg-emerald-600/90 border-emerald-400/40", pill: "bg-emerald-950/40 text-emerald-100" },
  heavy_check: { label: "Heavy Check", tile: "bg-blue-600/90 border-blue-300/40", pill: "bg-blue-950/40 text-blue-100" },
  mel: { label: "MEL", tile: "bg-amber-600/90 border-amber-300/40", pill: "bg-amber-950/40 text-amber-100" },
  capex: { label: "CAPEX", tile: "bg-rose-600/90 border-rose-300/40", pill: "bg-rose-950/40 text-rose-100" },
  unserviceable: { label: "Unserviceable", tile: "bg-red-700/95 border-red-400/50", pill: "bg-red-950/50 text-red-100" },
};

export default function FleetBoard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [live, setLive] = useState(false);
  const [spotIdx, setSpotIdx] = useState(0);
  const loadRef = useRef(() => {});

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/maintenance/board`);
      setData(r.data);
    } catch {}
  };
  loadRef.current = load;

  useEffect(() => {
    load();
    const clock = setInterval(() => setNow(new Date()), 1000);
    const poll = setInterval(() => loadRef.current(), 30000);
    let ws = null;
    let retry = null;
    let debounce = null;
    let closed = false;
    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(`${API_BASE.replace(/^http/, "ws")}/ws/maintenance`);
      } catch {
        retry = setTimeout(connect, 5000);
        return;
      }
      ws.onopen = () => setLive(true);
      ws.onmessage = () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => loadRef.current(), 250);
      };
      ws.onclose = () => {
        setLive(false);
        if (!closed) retry = setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      closed = true;
      clearInterval(clock);
      clearInterval(poll);
      clearTimeout(retry);
      clearTimeout(debounce);
      try { ws && ws.close(); } catch {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const list = data?.aircraft || [];
  const attention = list.filter((a) => a.mel_count > 0 || a.overdue_jobs > 0 || a.status === "unserviceable");
  const spotlight = attention.length > 0 ? attention[spotIdx % attention.length] : null;
  const melRows = list.flatMap((a) => (a.mel_items || []).map((m) => ({ tail: a.tail, ...m })));
  const aogRows = list.flatMap((a) => (a.aog_parts || []).map((p) => ({ tail: a.tail, ...p })));

  useEffect(() => {
    if (attention.length < 2) return undefined;
    const t = setInterval(() => setSpotIdx((i) => i + 1), 8000);
    return () => clearInterval(t);
  }, [attention.length]);

  const counts = {
    normal: list.filter((a) => a.status === "normal").length,
    heavy_check: list.filter((a) => a.status === "heavy_check").length,
    mel: list.filter((a) => a.status === "mel").length,
    unserviceable: list.filter((a) => a.status === "unserviceable").length,
  };
  const timeStr = now.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "America/Denver" });
  const dateStr = now.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric", timeZone: "America/Denver" });

  return (
    <div className="min-h-screen bg-[#0c1a2e] text-white px-6 py-5" data-testid="fleet-board">
      <div className="flex items-end justify-between flex-wrap gap-3 border-b border-white/10 pb-4">
        <div className="flex items-center gap-3">
          {user && (
            <Link
              to="/fleet/maintenance"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 font-mono text-[11px] uppercase tracking-widest text-white/80 transition-colors"
              title="Back to Maintenance"
              data-testid="board-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          )}
          <img src="/media/sa-logo.png" alt="Summit Air" className="w-10 h-10 object-contain bg-white rounded-lg" data-testid="board-logo" />
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight" data-testid="board-heading">Summit Air — Fleet Status</h1>
            <div className="font-mono text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-2">
              {dateStr} · MST
              <span className={`inline-flex items-center gap-1 ${live ? "text-emerald-300" : "text-white/40"}`} data-testid="board-live-dot">
                <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-400 animate-pulse" : "bg-white/30"}`} />
                {live ? "LIVE" : "POLLING"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-4 font-mono text-sm" data-testid="board-counts">
            <span className="text-emerald-300"><b className="text-xl">{counts.normal}</b> serviceable</span>
            <span className="text-blue-300"><b className="text-xl">{counts.heavy_check}</b> heavy check</span>
            <span className="text-amber-300"><b className="text-xl">{counts.mel}</b> MEL</span>
            <span className="text-red-300"><b className="text-xl">{counts.unserviceable}</b> unserviceable</span>
          </div>
          <div className="font-mono text-3xl md:text-4xl font-bold tabular-nums" data-testid="board-clock">{timeStr}</div>
        </div>
      </div>

      {spotlight && (
        <div className="mt-4 rounded-xl border border-white/15 bg-white/5 backdrop-blur px-5 py-3.5 flex items-start gap-5 flex-wrap" data-testid="board-spotlight">
          <div className="flex items-center gap-3 shrink-0">
            <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Spotlight
              {attention.length > 1 && <span className="text-white/40">{(spotIdx % attention.length) + 1}/{attention.length}</span>}
            </span>
            <span className="font-display text-3xl font-bold tracking-tight" data-testid="board-spotlight-tail">{spotlight.tail}</span>
            <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${(STATUS_META[spotlight.status] || STATUS_META.normal).tile}`}>
              {(STATUS_META[spotlight.status] || STATUS_META.normal).label}
            </span>
          </div>
          <div className="flex-1 min-w-[16rem] grid sm:grid-cols-2 gap-x-6 gap-y-1">
            {(spotlight.mel_items || []).slice(0, 4).map((m, i) => (
              <div key={i} className="flex items-center gap-2 font-mono text-[12px] text-amber-100" data-testid={`board-spotlight-mel-${i}`}>
                <span className="px-1.5 py-0.5 rounded bg-amber-400 text-amber-950 text-[9px] font-bold shrink-0">CAT {m.category}</span>
                <span className="truncate">{m.description}</span>
                {m.next_calendar_due && <span className="text-white/50 shrink-0">due {m.next_calendar_due}</span>}
              </div>
            ))}
            {(spotlight.overdue_titles || []).map((t, i) => (
              <div key={`o${i}`} className="flex items-center gap-2 font-mono text-[12px] text-red-200" data-testid={`board-spotlight-overdue-${i}`}>
                <span className="px-1.5 py-0.5 rounded bg-red-500 text-white text-[9px] font-bold shrink-0">OVERDUE</span>
                <span className="truncate">{t}</span>
              </div>
            ))}
            {spotlight.status === "unserviceable" && (
              <div className="font-mono text-[12px] text-red-200">
                {spotlight.us_reason && <span data-testid="board-spotlight-usreason">{spotlight.us_reason}</span>}
                {spotlight.us_return_to_service && <span className="text-white/50"> · RTS {spotlight.us_return_to_service.replace("T", " ")}</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {data === null ? (
        <div className="text-center text-white/40 py-24 font-mono text-sm">Loading fleet…</div>
      ) : (
        <div className="mt-5 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="board-grid">
          {list.map((a) => {
            const st = STATUS_META[a.status] || STATUS_META.normal;
            const isSpot = spotlight && spotlight.tail === a.tail;
            return (
              <div key={a.tail} className={`rounded-xl border ${st.tile} px-4 py-3 flex flex-col gap-1.5 shadow-lg transition-shadow ${isSpot ? "ring-4 ring-amber-300/80" : ""}`} data-testid={`board-tile-${a.tail}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-2xl xl:text-3xl font-bold tracking-tight leading-none">{a.tail}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mt-1">{a.fleet || ""}</div>
                  </div>
                  <Plane className="w-5 h-5 text-white/80 shrink-0" fill="currentColor" />
                </div>
                <div className={`self-start px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${st.pill}`} data-testid={`board-status-${a.tail}`}>
                  {st.label}
                </div>
                {a.status === "unserviceable" && a.us_reason && (
                  <div className="font-mono text-[11px] text-red-100/90 leading-snug" data-testid={`board-usreason-${a.tail}`}>
                    {a.us_reason}
                  </div>
                )}
                {a.status === "unserviceable" && a.us_return_to_service && (
                  <div className="font-mono text-[11px] text-red-100" data-testid={`board-rts-${a.tail}`}>
                    RTS {a.us_return_to_service.replace("T", " ")}
                  </div>
                )}
                <div className="mt-auto flex items-center gap-2 flex-wrap pt-1">
                  {a.mel_count > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/25 font-mono text-[10px]" data-testid={`board-mel-${a.tail}`}>
                      <FileWarning className="w-3 h-3" /> {a.mel_count} MEL
                    </span>
                  )}
                  {a.open_jobs > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/25 font-mono text-[10px]" data-testid={`board-jobs-${a.tail}`}>
                      <ClipboardList className="w-3 h-3" /> {a.open_jobs} jobs
                    </span>
                  )}
                  {a.overdue_jobs > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/90 font-mono text-[10px] font-bold" data-testid={`board-overdue-${a.tail}`}>
                      {a.overdue_jobs} OVERDUE
                    </span>
                  )}
                  {a.open_problems > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/40 font-mono text-[10px]" data-testid={`board-pr-${a.tail}`}>
                      <AlertTriangle className="w-3 h-3" /> {a.open_problems} PR
                    </span>
                  )}
                  {a.mel_count === 0 && a.open_jobs === 0 && a.open_problems === 0 && (
                    <span className="font-mono text-[10px] text-white/50">No open items</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {data !== null && (
        <div className="mt-8 space-y-6" data-testid="board-detail-tables">
          <div className="rounded-xl overflow-hidden border border-white/10 shadow-lg" data-testid="board-mel-table">
            <div className="bg-amber-500 text-white text-center py-2 font-display text-xl font-bold tracking-wide">MEL Items</div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-amber-500 text-white font-mono text-[11px] uppercase tracking-wider">
                    <th className="px-3 py-2">Tail#</th>
                    <th className="px-3 py-2">MEL Item</th>
                    <th className="px-3 py-2">Date Applied</th>
                    <th className="px-3 py-2">MEL# / Case# / Cat</th>
                    <th className="px-3 py-2">Expiry Date</th>
                    <th className="px-3 py-2">Ops Restriction?</th>
                    <th className="px-3 py-2">Parts on Order?</th>
                    <th className="px-3 py-2">Comment</th>
                  </tr>
                </thead>
                <tbody className="text-[13px] text-slate-800">
                  {melRows.length === 0 ? (
                    <tr className="bg-white"><td colSpan={8} className="text-center py-3 text-slate-500" data-testid="board-mel-empty">No MEL items!</td></tr>
                  ) : melRows.map((r, i) => (
                    <tr key={i} className={i % 2 ? "bg-slate-100" : "bg-white"} data-testid={`board-mel-row-${i}`}>
                      <td className="px-3 py-2 font-bold whitespace-nowrap">{r.tail}</td>
                      <td className="px-3 py-2">{r.description}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.date_applied || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{[r.mel_ref, r.category ? `Cat ${r.category}` : null].filter(Boolean).join(" / ")}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.next_calendar_due || "—"}</td>
                      <td className={`px-3 py-2 ${r.ops_restriction ? "text-red-600 font-bold" : ""}`}>{r.ops_restriction ? "Yes" : "No"}</td>
                      <td className="px-3 py-2">{r.parts_on_order ? "Yes" : "No"}</td>
                      <td className="px-3 py-2">{r.comment || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden border border-white/10 shadow-lg" data-testid="board-aog-table">
            <div className="bg-red-600 text-white text-center py-2 font-display text-xl font-bold tracking-wide">AOG Parts on Order</div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-red-600 text-white font-mono text-[11px] uppercase tracking-wider">
                    <th className="px-3 py-2">Tail#</th>
                    <th className="px-3 py-2">Part</th>
                    <th className="px-3 py-2">Ship To</th>
                    <th className="px-3 py-2">ETA</th>
                    <th className="px-3 py-2">PR/PO#</th>
                    <th className="px-3 py-2">Comment</th>
                  </tr>
                </thead>
                <tbody className="text-[13px] text-slate-800">
                  {aogRows.length === 0 ? (
                    <tr className="bg-white"><td colSpan={6} className="text-center py-3 text-slate-500" data-testid="board-aog-empty">No AOG parts on order!</td></tr>
                  ) : aogRows.map((r, i) => (
                    <tr key={i} className={i % 2 ? "bg-slate-100" : "bg-white"} data-testid={`board-aog-row-${i}`}>
                      <td className="px-3 py-2 font-bold whitespace-nowrap">{r.tail}</td>
                      <td className="px-3 py-2">{r.part}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.ship_to || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.eta || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.pr_po || "—"}</td>
                      <td className="px-3 py-2">{r.comment || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 text-center font-mono text-[10px] uppercase tracking-widest text-white/30">
        Updates automatically · {list.length} aircraft
      </div>
    </div>
  );
}
